import { scaleLinear } from "d3";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useResizeObserver } from "usehooks-ts";

type ColorKeyGradientProps = {
  data: { color: string; value: number }[];
  height?: number;
  title?: ReactNode;
  labels?: { label: string | number; value: number }[];
};

const ColorKeyGradient: React.FC<ColorKeyGradientProps> = ({
  data = [],
  height = 15,
  title,
  labels = [],
}) => {
  const [drawData, setDrawData] = useState<
    { color: string; value: number; x: number; width: number }[]
  >([]);
  const ref = useRef<HTMLDivElement>(null);
  const { width = 300 } = useResizeObserver({
    ref,
    box: "border-box",
  });

  const calculateDrawData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const totalWidth = width;

    // Create scale based on value range
    const valueScale = scaleLinear()
      .domain([Math.min(...data.map(d => d.value)), Math.max(...data.map(d => d.value))])
      .range([0, totalWidth]);

    // Calculate positions for each break point
    return data.map((item) => ({
      ...item,
      x: valueScale(item.value),
      width: totalWidth / (data.length - 1)
    }));
  }, [data, width]);

  useEffect(() => {
    setDrawData(calculateDrawData);
  }, [calculateDrawData]);

  if (!Array.isArray(data)) {
    return null;
  }

  // Create scale for label positions
  const valueScale = scaleLinear()
    .domain([Math.min(...data.map(d => d.value)), Math.max(...data.map(d => d.value))])
    .range([0, width]);

  return (
    <Container ref={ref} className="color-key-gradient">
      {title && <Title>{title}</Title>}
      <SVG width={width} height={height + 16}>
        <defs>
          <linearGradient id="colorGradient" x1="0" x2="1" y1="0" y2="0">
            <stop key="start" offset="0%" stopColor={drawData[0]?.color} />
            {drawData.map((item, index) => {
              const offset = (item.x / width) * 100;
              return (
                <stop
                  key={index}
                  offset={`${offset}%`}
                  stopColor={item.color}
                />
              );
            })}
            <stop
              key="final"
              offset="100%"
              stopColor={drawData[drawData.length - 1]?.color}
            />
          </linearGradient>
        </defs>
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="url(#colorGradient)"
        />
        {labels.map((labelItem, index) => {
          const x = valueScale(labelItem.value);
          const isFirst = index === 0;
          const isLast = index === labels.length - 1;
          return (
            <Label
              key={index}
              x={isLast ? width : x}
              y={height + 16}
              textAnchor={
                isFirst
                  ? "start"
                  : isLast
                  ? "end"
                  : "middle"
              }
            >
              {labelItem.label}
            </Label>
          );
        })}
      </SVG>
    </Container>
  );
};

export default ColorKeyGradient;

const Title = styled.div`
  font-size: 13px;
`;

const Container = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0px;
  row-gap: 0px;
  width: 100%;
  background-color: var(--site-background);
  /* max-width: 368px; */
  line-height: 1.2em;
  border-radius: 6px;
  /* box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2); */
  color: var(--text-color);
`;

const SVG = styled.svg`
  width: 100%;

  /* background-color: #f4f4f4; */
  overflow: visible;
`;

const Label = styled.text`
  font-size: 12px;
  fill: var(--text-color);
`;
